/**
 * EMZI NEXUS V6.4 - Live Studio V2
 * Step 9: Script Generation
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { EMZI_SPACING, EMZI_RADIUS } from '../../../styles/emziCI';
import * as api from '../../../api/liveStudioV2';

// Output format options
const OUTPUT_FORMATS = [
  { id: 'talking_points', icon: '📝', name: 'Talking Points', desc: 'Bullet points to guide your talk' },
  { id: 'full_script', icon: '📜', name: 'Full Script', desc: 'Complete word-by-word script' },
  { id: 'hybrid', icon: '🔀', name: 'Hybrid', desc: 'Mix of points and script' }
];

// Language style options
const LANGUAGE_STYLES = [
  { id: 'santai', icon: '😎', name: 'Santai', desc: 'Casual dan relax' },
  { id: 'pasar', icon: '💬', name: 'Pasar', desc: 'Bahasa pasar sehari-hari' },
  { id: 'formal', icon: '👔', name: 'Formal', desc: 'Professional dan formal' }
];

const Step9ScriptGeneration = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { language } = useLanguage();
  
  // State
  const [outputFormat, setOutputFormat] = useState('talking_points');
  const [languageStyle, setLanguageStyle] = useState('santai');
  const [isGenerating, setIsGenerating] = useState(false);
  const [script, setScript] = useState(sessionData?.script || null);

  const getText = (key) => {
    const translations = {
      en: {
        title: 'Script Generation',
        subtitle: 'Generate your live script based on SOA answers',
        formatLabel: 'Output Format',
        styleLabel: 'Language Style',
        generateBtn: '✨ Generate Script',
        generatingBtn: 'Generating...',
        regenerateBtn: '↻ Regenerate',
        scriptPreview: 'Script Preview',
        continueBtn: 'Continue to Submit →',
        totalCTAs: 'Total CTAs',
        duration: 'Duration',
        platform: 'Platform'
      },
      ms: {
        title: 'Jana Skrip',
        subtitle: 'Jana skrip live berdasarkan jawapan SOA',
        formatLabel: 'Format Output',
        styleLabel: 'Gaya Bahasa',
        generateBtn: '✨ Jana Skrip',
        generatingBtn: 'Menjana...',
        regenerateBtn: '↻ Jana Semula',
        scriptPreview: 'Pratonton Skrip',
        continueBtn: 'Teruskan ke Submit →',
        totalCTAs: 'Jumlah CTA',
        duration: 'Tempoh',
        platform: 'Platform'
      },
      bm: {
        title: 'Jana Skrip',
        subtitle: 'Jana skrip live berdasarkan jawapan SOA',
        formatLabel: 'Format Output',
        styleLabel: 'Gaya Bahasa',
        generateBtn: '✨ Jana Skrip',
        generatingBtn: 'Menjana...',
        regenerateBtn: '↻ Jana Semula',
        scriptPreview: 'Pratonton Skrip',
        continueBtn: 'Teruskan ke Submit →',
        totalCTAs: 'Jumlah CTA',
        duration: 'Tempoh',
        platform: 'Platform'
      },
      urban: {
        title: 'Script Generation',
        subtitle: 'Generate live script based on SOA answers korang',
        formatLabel: 'Output Format',
        styleLabel: 'Language Style',
        generateBtn: '✨ Generate Script',
        generatingBtn: 'Generating...',
        regenerateBtn: '↻ Regenerate',
        scriptPreview: 'Script Preview',
        continueBtn: 'Continue ke Submit →',
        totalCTAs: 'Total CTAs',
        duration: 'Duration',
        platform: 'Platform'
      }
    };
    return translations[language]?.[key] || translations.en[key];
  };

  // Generate script
  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const result = await api.generateLiveScript(token, sessionId, {
        outputFormat,
        languageStyle
      });
      
      setScript(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Continue to submit
  const handleContinue = () => {
    if (!script) {
      setError('Please generate script first');
      return;
    }
    
    setError(null);
    onComplete({
      scriptConfig: { outputFormat, languageStyle },
      script
    });
  };

  // Get section type color
  const getSectionColor = (type) => {
    const colors = {
      opening: '#28a745',
      product_story: '#0e67a2',
      engagement: '#667eea',
      objection_handling: '#fd7e14',
      closing: '#dc3545',
      cta: '#F89B29'
    };
    return colors[type] || '#666';
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: EMZI_SPACING.xl, textAlign: 'center' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: EMZI_SPACING.sm, color: '#fff' }}>
          🎬 {getText('title')}
        </h1>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>{getText('subtitle')}</p>
      </div>

      {/* Output Format Selection */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            📋 {getText('formatLabel')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', gap: '12px' }}>
            {OUTPUT_FORMATS.map(f => (
              <button
                key={f.id}
                onClick={() => setOutputFormat(f.id)}
                style={{
                  flex: 1,
                  padding: '16px',
                  borderRadius: '10px',
                  border: outputFormat === f.id ? '2px solid #0e67a2' : '1px solid #ddd',
                  background: outputFormat === f.id ? '#E8F4FF' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>{f.icon}</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: outputFormat === f.id ? '#0e67a2' : '#333' }}>
                  {f.name}
                </div>
                <div style={{ fontSize: '11px', color: '#999' }}>{f.desc}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Language Style Selection */}
      <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
            🎤 {getText('styleLabel')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', gap: '12px' }}>
            {LANGUAGE_STYLES.map(s => (
              <button
                key={s.id}
                onClick={() => setLanguageStyle(s.id)}
                style={{
                  flex: 1,
                  padding: '16px',
                  borderRadius: '10px',
                  border: languageStyle === s.id ? '2px solid #F89B29' : '1px solid #ddd',
                  background: languageStyle === s.id ? '#FFF5E6' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>{s.icon}</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: languageStyle === s.id ? '#F89B29' : '#333' }}>
                  {s.name}
                </div>
                <div style={{ fontSize: '11px', color: '#999' }}>{s.desc}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Generate Button */}
      <div style={{ textAlign: 'center', marginBottom: EMZI_SPACING.lg }}>
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          style={{
            padding: '16px 40px',
            background: script 
              ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '16px',
            fontWeight: '700',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            opacity: isGenerating ? 0.7 : 1,
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
          }}
        >
          {isGenerating 
            ? getText('generatingBtn') 
            : script 
              ? getText('regenerateBtn') 
              : getText('generateBtn')}
        </button>
      </div>

      {/* Script Preview */}
      {script && (
        <Card style={{ marginBottom: EMZI_SPACING.lg, borderRadius: EMZI_RADIUS.md }}>
          <CardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <CardTitle style={{ fontSize: '16px', fontWeight: '600' }}>
                📝 {getText('scriptPreview')}
              </CardTitle>
              <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                <span style={{ background: '#E8F4FF', padding: '4px 10px', borderRadius: '12px' }}>
                  {getText('platform')}: {script.platform}
                </span>
                <span style={{ background: '#E6F9E6', padding: '4px 10px', borderRadius: '12px' }}>
                  {getText('duration')}: {script.duration_minutes}min
                </span>
                <span style={{ background: '#FFF5E6', padding: '4px 10px', borderRadius: '12px' }}>
                  {getText('totalCTAs')}: {script.total_ctas}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {script.sections?.map((section, idx) => (
                <div
                  key={`item-${idx}`}
                  style={{
                    padding: '14px',
                    background: section.is_cta ? '#FFF5E6' : '#f8f9fa',
                    borderRadius: '10px',
                    borderLeft: `4px solid ${getSectionColor(section.type)}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: getSectionColor(section.type),
                      textTransform: 'uppercase'
                    }}>
                      {section.is_cta ? '📢 CTA' : section.type?.replace('_', ' ')}
                    </span>
                    <span style={{ fontSize: '11px', color: '#999' }}>
                      ⏰ {section.time}
                    </span>
                  </div>
                  <p style={{ fontSize: '14px', color: '#333', margin: 0, lineHeight: 1.5 }}>
                    {section.content}
                  </p>
                  {section.notes && (
                    <p style={{ fontSize: '11px', color: '#999', margin: '8px 0 0 0', fontStyle: 'italic' }}>
                      💡 {section.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: '14px',
            background: '#fff',
            border: '2px solid #0e67a2',
            borderRadius: '10px',
            color: '#0e67a2',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ← Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!script}
          style={{
            flex: 2,
            padding: '14px',
            background: script ? '#F7941E' : '#ccc',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: '600',
            cursor: script ? 'pointer' : 'not-allowed'
          }}
        >
          {getText('continueBtn')}
        </button>
      </div>
    </div>
  );
};

export default Step9ScriptGeneration;
